package com.patronat.top.features.venues.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.dp
import com.patronat.top.dto.venues.VenueGroup
import com.patronat.top.features.venues.presentation.ui.VenueLocationSection
import com.patronat.top.ui.atoms.AppTabSelector
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.models.OwnerInfoData
import com.patronat.top.ui.models.VenueGalleryPhoto
import com.patronat.top.ui.models.VenueLocationData
import com.patronat.top.ui.models.VenueMainInfoData
import com.patronat.top.ui.models.VenueModeratorOption
import com.patronat.top.ui.models.VenueServiceItemData
import com.patronat.top.ui.models.WorkScheduleDay
import com.patronat.top.ui.molecules.actions.FormFooterActions
import com.patronat.top.ui.organisms.schedule.WorkScheduleSection
import com.patronat.top.ui.organisms.venue.MainInfoSection
import com.patronat.top.ui.organisms.venue.OwnerInfoSection
import com.patronat.top.ui.organisms.venue.ServicesSection
import com.patronat.top.ui.organisms.venue.VenueModeratorSelector
import com.patronat.top.ui.organisms.venue.VenueGallerySection
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.MineShaft50
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.*

enum class VenueFormTab {
    Venue,
    Services;

    companion object {
        fun fromIndex(index: Int): VenueFormTab = entries.getOrElse(index) { Venue }
    }
}

@Composable
private fun VenueFormTab.title(): String {
    return when (this) {
        VenueFormTab.Venue -> stringResource(Res.string.venue_tab_venue)
        VenueFormTab.Services -> stringResource(Res.string.venue_tab_services)
    }
}

@Composable
fun VenueFormScreen(
    ownerInfo: OwnerInfoData,
    onOwnerInfoChange: (OwnerInfoData) -> Unit,
    mainInfo: VenueMainInfoData,
    onMainInfoChange: (VenueMainInfoData) -> Unit,
    mainInfoNameError: Boolean = false,
    mainInfoCategoryError: Boolean = false,
    mainInfoPhoneError: Boolean = false,
    mainInfoWebsiteError: Boolean = false,
    mainInfoDescriptionError: Boolean = false,
    scheduleDays: List<WorkScheduleDay>,
    onScheduleChange: (List<WorkScheduleDay>) -> Unit,
    scheduleError: Boolean = false,
    locationData: VenueLocationData = VenueLocationData(),
    onLocationDataChange: (VenueLocationData) -> Unit = {},
    locationAddressError: Boolean = false,
    venueMarkers: List<VenueMapMarker> = emptyList(),
    onMarkerClicked: (VenueMapMarker) -> Unit = {},
    galleryPhotos: List<VenueGalleryPhoto> = emptyList(),
    onGalleryPhotosChange: (List<VenueGalleryPhoto>) -> Unit = {},
    galleryError: Boolean = false,
    galleryErrorPhotoIds: Set<String> = emptySet(),
    onGalleryAddClick: () -> Unit = {},
    services: List<VenueServiceItemData> = emptyList(),
    onServicesChange: (List<VenueServiceItemData>) -> Unit = {},
    serviceErrorIds: Set<String> = emptySet(),
    selectedTab: VenueFormTab = VenueFormTab.Venue,
    onSelectedTabChange: (VenueFormTab) -> Unit = {},
    showModeratorSelector: Boolean = false,
    moderatorTitlePrefix: String = "Модератор:",
    moderatorSelectedName: String = "",
    moderatorOptions: List<VenueModeratorOption> = emptyList(),
    moderatorSelectorExpanded: Boolean = false,
    moderatorSelectorEnabled: Boolean = true,
    moderatorSelectorLoading: Boolean = false,
    onModeratorMenuExpandedChange: (Boolean) -> Unit = {},
    onModeratorSelected: (String) -> Unit = {},
    showOwnerSection: Boolean = true,
    attachedServicesFileName: String? = null,
    onUploadServicesFileClick: () -> Unit = {},
    onServicesFileRemoveClick: (() -> Unit)? = null,
    onAddServiceClick: () -> Unit = {},
    onSaveServicesDraftClick: () -> Unit = {},
    onSubmitServicesClick: () -> Unit = {},
    showVenueFooterActions: Boolean = true,
    onVenueCancelClick: () -> Unit = {},
    onVenueSubmitClick: () -> Unit = {},
    venueSubmitLoading: Boolean = false,
    modifier: Modifier = Modifier,
    ownerReadOnly: Boolean = true,
    ownerIcon: Painter? = null,
    mainInfoIcon: Painter? = null,
    locationIcon: Painter? = null,
    scheduleIcon: Painter? = null,
    galleryIcon: Painter? = null,
    servicesUploadIcon: Painter? = AppIcons.Upload,
    servicesFileIcon: Painter? = AppIcons.File,
    servicesHeaderIcon: Painter? = AppIcons.Profile,
    servicesFieldIcon: Painter? = AppIcons.Tag,
    servicesSubmitIcon: Painter? = AppIcons.Shield,
    venueCancelButtonText: String? = null,
    venueSubmitButtonText: String? = null,
    serviceUploadButtonText: String? = null,
    serviceAddButtonText: String? = null,
    serviceSaveDraftButtonText: String? = null,
    serviceSubmitButtonText: String? = null,
    serviceCardTitle: String? = null,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    showBackground: Boolean = false
) {
    val ownerSectionTitle = stringResource(Res.string.venue_owner_title)

    val ownerFirstNameLabel = stringResource(Res.string.venue_owner_first_name_label)
    val ownerLastNameLabel = stringResource(Res.string.venue_owner_last_name_label)
    val ownerPhoneLabel = stringResource(Res.string.venue_owner_phone_label)
    val ownerEmailLabel = stringResource(Res.string.venue_owner_email_label)

    val mainInfoSectionTitle = stringResource(Res.string.venue_main_info_title)
    val mainInfoNameLabel = stringResource(Res.string.venue_main_info_name_label)
    val mainInfoCategoryLabel = stringResource(Res.string.venue_main_info_category_label)
    val mainInfoCategoryPlaceholder = stringResource(Res.string.venue_main_info_category_placeholder)
    val mainInfoCategorySearchPlaceholder = stringResource(Res.string.venue_category_search_placeholder)
    val mainInfoCategoryEmptyResults = stringResource(Res.string.venue_category_empty_results)
    val mainInfoPhoneLabel = stringResource(Res.string.venue_main_info_phone_label)
    val mainInfoWebsiteLabel = stringResource(Res.string.venue_main_info_website_label)
    val mainInfoDescriptionLabel = stringResource(Res.string.venue_main_info_description_label)

    val locationTitle = stringResource(Res.string.venue_location_title)
    val locationAddressLabel = stringResource(Res.string.venue_location_address_label)
    val locationAddressPlaceholder = stringResource(Res.string.venue_location_address_placeholder)

    val scheduleTitle = stringResource(Res.string.venue_schedule_title)

    val resolvedVenueCancelButtonText = venueCancelButtonText ?: stringResource(Res.string.venue_button_cancel)
    val resolvedVenueSubmitButtonText = venueSubmitButtonText ?: stringResource(Res.string.venue_button_submit)
    val resolvedServiceUploadButtonText = serviceUploadButtonText ?: stringResource(Res.string.venue_service_upload_file)
    val resolvedServiceAddButtonText = serviceAddButtonText ?: stringResource(Res.string.venue_service_add)
    val resolvedServiceSaveDraftButtonText = serviceSaveDraftButtonText ?: stringResource(Res.string.venue_service_save_draft)
    val resolvedServiceSubmitButtonText = serviceSubmitButtonText ?: stringResource(Res.string.venue_service_submit)
    val resolvedServiceCardTitle = serviceCardTitle ?: stringResource(Res.string.venue_service_card_title)

    val categoryGroups = venueCategoryGroups()
    val tabTitles = VenueFormTab.entries.map { it.title() }

    val backgroundModifier = if (showBackground) {
        Modifier.background(MineShaft50)
    } else {
        Modifier
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .then(backgroundModifier)
            .verticalScroll(rememberScrollState())
            .padding(contentPadding),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        when (selectedTab) {
            VenueFormTab.Venue -> {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.Bottom
                ) {
                    AppTabSelector(
                        options = tabTitles,
                        selectedIndex = selectedTab.ordinal,
                        onOptionSelected = { onSelectedTabChange(VenueFormTab.fromIndex(it)) },
                        modifier = Modifier.weight(1f)
                    )

                    if (showModeratorSelector) {
                        VenueModeratorSelector(
                            titlePrefix = moderatorTitlePrefix,
                            selectedName = moderatorSelectedName,
                            options = moderatorOptions,
                            expanded = moderatorSelectorExpanded,
                            enabled = moderatorSelectorEnabled,
                            isLoading = moderatorSelectorLoading,
                            onExpandedChange = onModeratorMenuExpandedChange,
                            onOptionSelected = { option -> onModeratorSelected(option.id) },
                            leadingIcon = AppIcons.VenueModeratorAssign,
                            modifier = Modifier.width(343.dp)
                        )
                    }
                }

                if (showOwnerSection) {
                    OwnerInfoSection(
                        data = ownerInfo,
                        onDataChange = onOwnerInfoChange,
                        readOnly = ownerReadOnly,
                        leadingIcon = ownerIcon ?: AppIcons.VenueOwner,
                        title = ownerSectionTitle,
                        firstNameLabel = ownerFirstNameLabel,
                        lastNameLabel = ownerLastNameLabel,
                        phoneLabel = ownerPhoneLabel,
                        emailLabel = ownerEmailLabel
                    )
                }

                MainInfoSection(
                    data = mainInfo,
                    onDataChange = onMainInfoChange,
                    nameState = if (mainInfoNameError) InputState.Error else null,
                    categoryState = if (mainInfoCategoryError) InputState.Error else null,
                    phoneState = if (mainInfoPhoneError) InputState.Error else null,
                    websiteState = if (mainInfoWebsiteError) InputState.Error else null,
                    descriptionState = if (mainInfoDescriptionError) InputState.Error else null,
                    leadingIcon = mainInfoIcon ?: AppIcons.VenueMainInfo,
                    title = mainInfoSectionTitle,
                    nameLabel = mainInfoNameLabel,
                    categoryLabel = mainInfoCategoryLabel,
                    categoryPlaceholder = mainInfoCategoryPlaceholder,
                    categoryGroups = categoryGroups,
                    categorySearchPlaceholder = mainInfoCategorySearchPlaceholder,
                    categoryEmptyResultsText = mainInfoCategoryEmptyResults,
                    categoryGroupIconProvider = { group ->
                        when (group.id) {
                            VenueGroup.FOOD.name -> AppIcons.VenueGroupFood
                            VenueGroup.SHOPPING.name -> AppIcons.VenueGroupShopping
                            VenueGroup.BEAUTY.name -> AppIcons.VenueGroupBeauty
                            VenueGroup.HEALTH.name -> AppIcons.VenueGroupHealth
                            VenueGroup.AUTO.name -> AppIcons.VenueGroupAuto
                            VenueGroup.HOME.name -> AppIcons.VenueGroupHome
                            VenueGroup.EDUCATION.name -> AppIcons.VenueGroupEducation
                            VenueGroup.BUSINESS.name -> AppIcons.VenueGroupBusiness
                            VenueGroup.IT_MEDIA.name -> AppIcons.VenueGroupItMedia
                            VenueGroup.LOGISTICS.name -> AppIcons.VenueGroupLogistics
                            VenueGroup.ENTERTAINMENT.name -> AppIcons.VenueGroupEntertainment
                            VenueGroup.PETS.name -> AppIcons.VenueGroupPets
                            VenueGroup.HOUSEHOLD.name -> AppIcons.VenueGroupHousehold
                            VenueGroup.EVENTS.name -> AppIcons.VenueGroupEvents
                            VenueGroup.TRAVEL.name -> AppIcons.VenueGroupTravel
                            else -> AppIcons.VenueGroupFood
                        }
                    },
                    phoneLabel = mainInfoPhoneLabel,
                    websiteLabel = mainInfoWebsiteLabel,
                    descriptionLabel = mainInfoDescriptionLabel
                )

                VenueLocationSection(
                    data = locationData,
                    onDataChange = onLocationDataChange,
                    addressError = locationAddressError,
                    leadingIcon = locationIcon ?: AppIcons.VenueLocation,
                    title = locationTitle,
                    addressLabel = locationAddressLabel,
                    addressPlaceholder = locationAddressPlaceholder,
                    venueMarkers = venueMarkers,
                    onMarkerClicked = onMarkerClicked
                )

                WorkScheduleSection(
                    title = scheduleTitle,
                    days = scheduleDays,
                    onDaysChange = onScheduleChange,
                    highlightError = scheduleError,
                    leadingIcon = scheduleIcon ?: AppIcons.VenueSchedule
                )

                VenueGallerySection(
                    photos = galleryPhotos,
                    highlightError = galleryError,
                    errorPhotoIds = galleryErrorPhotoIds,
                    onUploadClick = onGalleryAddClick,
                    onPhotoRemoveClick = { photo ->
                        onGalleryPhotosChange(galleryPhotos.filterNot { it.id == photo.id })
                    },
                    leadingIcon = galleryIcon ?: AppIcons.VenueGallery
                )

                if (showVenueFooterActions) {
                    FormFooterActions(
                        cancelText = resolvedVenueCancelButtonText,
                        submitText = resolvedVenueSubmitButtonText,
                        onCancelClick = onVenueCancelClick,
                        onSubmitClick = onVenueSubmitClick,
                        submitLeadingIcon = servicesSubmitIcon,
                        submitLoading = venueSubmitLoading
                    )
                }
            }

            VenueFormTab.Services -> {
                ServicesSection(
                    services = services,
                    errorServiceIds = serviceErrorIds,
                    onServiceChange = { updated ->
                        onServicesChange(services.replaceService(updated))
                    },
                    onServiceRemoveClick = { item ->
                        onServicesChange(services.filterNot { it.id == item.id })
                    },
                    onAddServiceClick = onAddServiceClick,
                    onUploadFileClick = onUploadServicesFileClick,
                    onCancelClick = onSaveServicesDraftClick,
                    onSubmitClick = onSubmitServicesClick,
                    selectedTabIndex = selectedTab.ordinal,
                    onTabSelected = { index ->
                        onSelectedTabChange(VenueFormTab.fromIndex(index))
                    },
                    attachedFileName = attachedServicesFileName,
                    onRemoveFileClick = onServicesFileRemoveClick,
                    uploadButtonText = resolvedServiceUploadButtonText,
                    addButtonText = resolvedServiceAddButtonText,
                    cancelButtonText = resolvedServiceSaveDraftButtonText,
                    submitButtonText = resolvedServiceSubmitButtonText,
                    serviceCardTitle = resolvedServiceCardTitle,
                    uploadIcon = servicesUploadIcon,
                    fileIcon = servicesFileIcon,
                    serviceHeaderIcon = servicesHeaderIcon,
                    serviceFieldIcon = servicesFieldIcon,
                    submitLeadingIcon = servicesSubmitIcon
                )
            }
        }
    }
}

@Composable
fun VenueFormDemoScreen(
    modifier: Modifier = Modifier,
    pickServicesPdfFileName: suspend () -> String? = { null },
    pickGalleryImage: suspend () -> VenueGalleryPhoto? = { null }
) {
    val ownerFirstName = stringResource(Res.string.venue_demo_owner_first_name)
    val ownerLastName = stringResource(Res.string.venue_demo_owner_last_name)
    val ownerPhone = stringResource(Res.string.venue_demo_owner_phone)
    val ownerEmail = stringResource(Res.string.venue_demo_owner_email)

    val venueName = stringResource(Res.string.venue_demo_name)
    val venuePhone = stringResource(Res.string.venue_demo_phone)
    val venueWebsite = stringResource(Res.string.venue_demo_website)
    val venueAddress = stringResource(Res.string.venue_demo_address)

    val dayMon = stringResource(Res.string.venue_day_mon)
    val dayTue = stringResource(Res.string.venue_day_tue)
    val dayWed = stringResource(Res.string.venue_day_wed)
    val dayThu = stringResource(Res.string.venue_day_thu)
    val dayFri = stringResource(Res.string.venue_day_fri)
    val daySat = stringResource(Res.string.venue_day_sat)
    val daySun = stringResource(Res.string.venue_day_sun)

    val service1Name = stringResource(Res.string.venue_demo_service_1_name)
    val service1Price = stringResource(Res.string.venue_demo_service_1_price)
    val service1Currency = stringResource(Res.string.venue_demo_service_1_currency)
    val service1Description = stringResource(Res.string.venue_demo_service_1_description)
    val service2Name = stringResource(Res.string.venue_demo_service_2_name)
    val service2Price = stringResource(Res.string.venue_demo_service_2_price)
    val service2Currency = stringResource(Res.string.venue_demo_service_2_currency)

    var ownerInfo by remember(ownerFirstName, ownerLastName, ownerPhone, ownerEmail) {
        mutableStateOf(
            OwnerInfoData(
                firstName = ownerFirstName,
                lastName = ownerLastName,
                phone = ownerPhone,
                email = ownerEmail
            )
        )
    }

    var mainInfo by remember(venueName, venuePhone, venueWebsite) {
        mutableStateOf(
            VenueMainInfoData(
                name = venueName,
                phone = venuePhone,
                website = venueWebsite,
                description = ""
            )
        )
    }

    var schedule by remember(dayMon, dayTue, dayWed, dayThu, dayFri, daySat, daySun) {
        mutableStateOf(defaultWorkSchedule(dayMon, dayTue, dayWed, dayThu, dayFri, daySat, daySun))
    }
    var locationData by remember(venueAddress) { mutableStateOf(defaultLocationData(venueAddress)) }
    var galleryPhotos by remember { mutableStateOf(defaultGalleryPhotos()) }
    var selectedTab by remember { mutableStateOf(VenueFormTab.Venue) }
    var services by remember(
        service1Name,
        service1Price,
        service1Currency,
        service1Description,
        service2Name,
        service2Price,
        service2Currency
    ) {
        mutableStateOf(
            defaultServices(
                service1Name = service1Name,
                service1Price = service1Price,
                service1Currency = service1Currency,
                service1Description = service1Description,
                service2Name = service2Name,
                service2Price = service2Price,
                service2Currency = service2Currency
            )
        )
    }
    var attachedServicesFileName by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()

    VenueFormScreen(
        ownerInfo = ownerInfo,
        onOwnerInfoChange = { ownerInfo = it },
        mainInfo = mainInfo,
        onMainInfoChange = { mainInfo = it },
        scheduleDays = schedule,
        onScheduleChange = { schedule = it },
        locationData = locationData,
        onLocationDataChange = { locationData = it },
        galleryPhotos = galleryPhotos,
        onGalleryPhotosChange = { galleryPhotos = it },
        services = services,
        onServicesChange = { services = it },
        selectedTab = selectedTab,
        onSelectedTabChange = { selectedTab = it },
        attachedServicesFileName = attachedServicesFileName,
        onUploadServicesFileClick = {
            coroutineScope.launch {
                val pickedFileName = pickServicesPdfFileName()
                if (!pickedFileName.isNullOrBlank()) {
                    attachedServicesFileName = pickedFileName
                }
            }
        },
        onServicesFileRemoveClick = { attachedServicesFileName = null },
        onAddServiceClick = {
            services = services + VenueServiceItemData(
                id = "service_${services.size + 1}"
            )
        },
        onSaveServicesDraftClick = {},
        onSubmitServicesClick = {},
        onGalleryAddClick = {
            coroutineScope.launch {
                val pickedImage = pickGalleryImage()
                if (pickedImage != null) {
                    galleryPhotos = galleryPhotos + pickedImage
                }
            }
        },
        modifier = modifier
    )
}

private fun defaultWorkSchedule(
    dayMon: String,
    dayTue: String,
    dayWed: String,
    dayThu: String,
    dayFri: String,
    daySat: String,
    daySun: String
): List<WorkScheduleDay> {
    return listOf(
        WorkScheduleDay("mon", dayMon, true, "09:00", "18:00"),
        WorkScheduleDay("tue", dayTue, true, "09:00", "18:00"),
        WorkScheduleDay("wed", dayWed, true, "09:00", "18:00"),
        WorkScheduleDay("thu", dayThu, true, "09:00", "18:00"),
        WorkScheduleDay("fri", dayFri, true, "09:00", "18:00"),
        WorkScheduleDay("sat", daySat, false, "", ""),
        WorkScheduleDay("sun", daySun, false, "", "")
    )
}

private fun defaultServices(
    service1Name: String,
    service1Price: String,
    service1Currency: String,
    service1Description: String,
    service2Name: String,
    service2Price: String,
    service2Currency: String
): List<VenueServiceItemData> {
    return listOf(
        VenueServiceItemData(
            id = "service_1",
            name = service1Name,
            price = service1Price,
            currency = service1Currency,
            description = service1Description
        ),
        VenueServiceItemData(
            id = "service_2",
            name = service2Name,
            price = service2Price,
            currency = service2Currency,
            description = "",
            hasError = true
        )
    )
}

private fun defaultLocationData(address: String): VenueLocationData {
    return VenueLocationData(
        address = address,
        latitude = 50.4501,
        longitude = 30.5234,
        mapPreviewUrl = ""
    )
}

private fun defaultGalleryPhotos(): List<VenueGalleryPhoto> {
    return listOf(
        VenueGalleryPhoto(id = "photo_1", imageUrl = "https://picsum.photos/id/1060/600/600"),
        VenueGalleryPhoto(id = "photo_2", imageUrl = "https://picsum.photos/id/1035/600/600"),
        VenueGalleryPhoto(id = "photo_3", imageUrl = "https://picsum.photos/id/1025/600/600"),
        VenueGalleryPhoto(id = "photo_4", imageUrl = "https://picsum.photos/id/1084/600/600")
    )
}

private fun List<VenueServiceItemData>.replaceService(
    updated: VenueServiceItemData
): List<VenueServiceItemData> {
    return map { if (it.id == updated.id) updated else it }
}
